import { Module } from "vuex";
import { State } from "../store";
import { httpsCallable } from "firebase/functions";
import { functions } from "@/plugins/firebase";
import { PLAY_STYLE_STRING, PlayStyle } from "@/common/types/PlayStyle";
import { MusicQueryData } from "@/common/types/MusicQueryData";
import { deserializeDate, serializeDate, SerializedDate } from "@/common/types/SerializedDate";
import cloneDeep from "lodash/cloneDeep";
import isEqual from "lodash/isEqual";
import { Pref } from "@/common/types/Pref";

export type MusicQueryState = {
  sp: MusicQueryData[] | null;
  dp: MusicQueryData[] | null;
};

const getStateKey = (playStyle: PlayStyle) => PLAY_STYLE_STRING[playStyle].toLowerCase() as keyof MusicQueryState;

const musicQueryModule: Module<MusicQueryState, State> = {
  namespaced: true,
  state: {
    sp: null,
    dp: null,
  },
  mutations: {
    setMusicQueryArray(state, { playStyle, musicQuery }: { playStyle: PlayStyle; musicQuery: MusicQueryData[] }) {
      state[getStateKey(playStyle)] = cloneDeep(musicQuery);
    },
    addMusicQuery(state, musicQuery: MusicQueryData) {
      const musicQueryArray = state[getStateKey(musicQuery.playStyle)];
      if (!musicQueryArray) {
        throw new Error("musicQueryArray is null.");
      }
      musicQueryArray.unshift(cloneDeep(musicQuery));
    },
  },
  actions: {
    async getMusicQueryArray({ state, commit }, playStyle: PlayStyle) {
      if (state[getStateKey(playStyle)] !== null) {
        return state[getStateKey(playStyle)];
      }
      const fn = httpsCallable<
        { playStyle: PlayStyle },
        SerializedDate<{ musicQuery: MusicQueryData[]; weight: number[] }>
      >(functions, "musicQuery-get");

      const { musicQuery, weight } = deserializeDate((await fn({ playStyle })).data);
      const result = musicQuery.sort((a, b) => weight.indexOf(a.id) - weight.indexOf(b.id));

      commit("setMusicQueryArray", { playStyle, musicQuery: result });
      return cloneDeep(result);
    },
    async setMusicQueryArray(
      { state, commit },
      { playStyle, musicQuery }: { playStyle: PlayStyle; musicQuery: MusicQueryData[] }
    ) {
      const prevMusicQuery = state[getStateKey(playStyle)];
      if (prevMusicQuery) {
        let removeId = prevMusicQuery.map((e) => e.id);
        const id = musicQuery.map((e) => e.id);
        removeId = removeId.filter((e) => !id.includes(e));

        const updateQuery = musicQuery.filter(
          (e) =>
            !isEqual(
              e,
              prevMusicQuery.find((f) => f.id === e.id)
            )
        );

        const weight = musicQuery.map((e) => e.id);

        const remove = httpsCallable(functions, "musicQuery-remove");
        const update = httpsCallable(functions, "musicQuery-update");
        const updateWeight = httpsCallable(functions, "musicQuery-updateWeight");

        await Promise.all(removeId.map((e: number) => remove({ id: e })))
          .then(() => Promise.all(updateQuery.map((e) => update(serializeDate(e)))))
          .then(() => updateWeight({ playStyle: playStyle, weight: weight }));
      }

      commit("setMusicQueryArray", { playStyle, musicQuery });
    },
    async addMusicQuery({ commit, dispatch }, musicQuery: Omit<MusicQueryData, "id" | "playStyle">) {
      await dispatch("getMusicQueryArray", musicQuery.query.bestRecordsCriteria1.playStyle);
      const fn = httpsCallable<Omit<MusicQueryData, "id" | "playStyle">, SerializedDate<MusicQueryData>>(
        functions,
        "musicQuery-add"
      );
      const musicQueryData = deserializeDate((await fn(musicQuery)).data);

      commit("addMusicQuery", musicQueryData);
    },
    async getMusicQuery({ dispatch }, { playStyle, id }: { playStyle: PlayStyle; id: number }) {
      const musicQueryArray: MusicQueryData[] = await dispatch("getMusicQueryArray", playStyle);
      return cloneDeep(musicQueryArray.find((e) => e.id === id));
    },
    getDefaultMusicQuery({ dispatch, rootGetters }, playStyle: PlayStyle) {
      const pref: Pref = rootGetters["pref/getPref"];
      return dispatch("getMusicQuery", { playStyle, id: pref.defaultMusicQuery[getStateKey(playStyle)] });
    },
  },
};

export default musicQueryModule;
